import { Card, Layout, Menu, theme, type MenuProps } from 'antd';
import { controlHeightTokens, marginTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { ColorScheme, FontSizes } from './types';

const { Sider, Content } = Layout;

interface VendorTabsProps {
  activeSection: string;
  menuItems: MenuProps['items'];
  onSectionChange: (key: string) => void;
  children: React.ReactNode;
  currentColorScheme: ColorScheme;
  currentFontSizes: FontSizes;
}

export const VendorTabs = ({
  activeSection,
  menuItems,
  onSectionChange,
  currentColorScheme,
  currentFontSizes,
  children,
}: VendorTabsProps) => {
  const { token } = theme.useToken();
  const elevation = useElevation();

  return (
    <Card
      style={{
        ...elevation.card,
        padding: 0,
        overflow: 'hidden',
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Layout style={{ backgroundColor: 'transparent' }}>
        <Sider
          width={248}
          theme="light"
          style={{
            borderRight: `1px solid ${currentColorScheme.borderColor}`,
            padding: 0,
            backgroundColor: 'transparent',
          }}
        >
          {/* Custom CSS for menu item heights */}
          <style>
            {`
              .vendor-detail-menu {
                padding: 0 12px !important;
              }
              .vendor-detail-menu .ant-menu-item {
                height: ${controlHeightTokens.controlHeightXL}px !important;
                line-height: ${controlHeightTokens.controlHeightXL}px !important;
                margin: 4px 0 !important;
                /* 12px, not 16 — the item chrome plus AntD's icon gap left the label
                   column too narrow for 「Bank Payment Details」 / 「Financial
                   Information」 at this 248px rail. Trimmed to the marginSM rung so
                   every label fits after the Menu icon gap moved to marginXS (8px);
                   see the Menu.iconMarginInlineEnd note in styles/theme.ts. */
                padding-left: ${marginTokens.marginSM}px !important;
                padding-right: ${marginTokens.marginSM}px !important;
                display: flex !important;
                align-items: center !important;
                border-radius: ${radiusTokens.card}px !important;
                width: 100% !important;
                font-size: ${currentFontSizes.menuItem}px !important;
              }
              .vendor-detail-menu .ant-menu-item-selected {
                background-color: ${currentColorScheme.highlightBg} !important;
                color: ${currentColorScheme.headerAccent} !important;
              }
              .vendor-detail-menu .ant-menu-item:hover {
                background-color: ${token.colorBgTextHover} !important;
              }
              .vendor-detail-menu .ant-menu-item .ant-menu-item-icon {
                font-size: ${currentFontSizes.menuItem}px !important;
              }
              .vendor-detail-menu .ant-menu-item .ant-menu-title-content {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                font-size: ${currentFontSizes.menuItem}px !important;
              }
            `}
          </style>
          <Menu
            mode="inline"
            selectedKeys={[activeSection]}
            items={menuItems}
            onClick={({ key }) => onSectionChange(key)}
            className="vendor-detail-menu"
            style={{ borderRight: 'none', backgroundColor: 'transparent' }}
          />
        </Sider>
        <Content style={{ padding: '24px', minHeight: '400px', backgroundColor: 'transparent' }}>
          {children}
        </Content>
      </Layout>
    </Card>
  );
};

export default VendorTabs;
